import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.pics';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Der Ultimative OOPBuy Spreadsheet Guide Für Deutschland 2026',
  description: 'Alles, was du über das OOPBuy Spreadsheet wissen musst: Von der Einrichtung über die besten Kategorien bis hin zu Sicherheitstipps und Versandoptionen für den deutschen Markt.',
  author: {
    '@type': 'Organization',
    name: 'OOPBuy Editorial Team',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'OOPBuy Spreadsheet',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  datePublished: '2026-07-15',
  dateModified: '2026-07-15',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/artikel/oopbuy-spreadsheet-ratgeber`,
  },
  inLanguage: 'de',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Was ist das OOPBuy Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Das OOPBuy Spreadsheet ist ein umfassendes Tool für internationale Mode-Einkäufe, das speziell für den deutschen Markt optimiert wurde.',
      },
    },
    {
      '@type': 'Question',
      name: 'Wie oft wird das Spreadsheet aktualisiert?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Unser OOPBuy Spreadsheet wird wöchentlich aktualisiert. Neue Produkte werden hinzugefügt und Preise angepasst.',
      },
    },
    {
      '@type': 'Question',
      name: 'Ist das OOPBuy Spreadsheet kostenlos?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Ja, der Zugang zu unserem Spreadsheet und allen Kategorien ist vollständig kostenlos.',
      },
    },
    {
      '@type': 'Question',
      name: 'Wie funktioniert der Versand nach Deutschland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Das Spreadsheet enthält detaillierte Versandinformationen. Die meisten Bestellungen erreichen dich innerhalb von 10-20 Werktagen.',
      },
    },
    {
      '@type': 'Question',
      name: 'Sind die Verkäufer im Spreadsheet vertrauenswürdig?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Alle Verkäufer durchlaufen einen strengen Verifizierungsprozess mit Bewertungsanalyse und Testkäufen.',
      },
    },
  ],
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Startseite',
      item: SITE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'OOPBuy Spreadsheet Ratgeber',
      item: `${SITE_URL}/artikel/oopbuy-spreadsheet-ratgeber`,
    },
  ],
};

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = '2026-07-15';

  return (
    <>
      <Navbar />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <article className="min-h-screen bg-background-50 mt-16 md:mt-[4.5rem]">
        {/* Article Header with Breadcrumbs */}
        <header className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 font-[family-name:var(--font-label)]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">
                Startseite
              </Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-800">OOPBuy Spreadsheet Ratgeber</span>
            </nav>

            <p className="text-xs uppercase tracking-[0.2em] text-primary-600 mb-3 font-[family-name:var(--font-label)]">
              Ratgeber & Anleitung
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              Der Ultimative OOPBuy Spreadsheet Guide Für Deutschland 2026
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl font-[family-name:var(--font-body)]">
              Alles, was du über das OOPBuy Spreadsheet wissen musst: Von der Einrichtung über die besten Kategorien bis hin zu Sicherheitstipps und Versandoptionen für den deutschen Markt.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-primary-600"></i>
                </div>
                <span className="text-xs text-foreground-600 font-[family-name:var(--font-label)]">OOPBuy Editorial Team</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-sm text-foreground-400"></i>
                <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">
                  Veröffentlicht: {currentDate}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-sm text-foreground-400"></i>
                <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">Lesezeit: 12 Minuten</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-refresh-line text-sm text-foreground-400"></i>
                <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">
                  Aktualisiert: {currentDate}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Table of Contents */}
        <nav className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8" aria-label="Inhaltsverzeichnis">
          <div className="bg-background-100 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-foreground-800 mb-4 font-[family-name:var(--font-label)]">
              Inhaltsverzeichnis
            </h2>
            <ul className="space-y-2">
              {tocItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer font-[family-name:var(--font-body)]"
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Article Content */}
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
          <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed font-[family-name:var(--font-body)]">

            {/* Section 1 */}
            <section id="einleitung">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Einleitung: Warum das OOPBuy Spreadsheet Dein Shopping-Erlebnis Revolutioniert
              </h2>
              <p>
                In der heutigen digitalen Einkaufswelt kann die Suche nach den besten Angeboten schnell 
                überwältigend werden. Genau hier setzt das <strong>OOPBuy Spreadsheet</strong> an - ein 
                durchdachtes, für den deutschen Markt optimiertes Tool, das Ordnung in das Chaos 
                internationaler Mode-Bestellungen bringt. Ob du auf der Suche nach limitierten 
                <strong>Sneakern</strong>, hochwertigen <strong>Designerjacken</strong> oder 
                angesagten <strong>Accessoires</strong> bist - unser Spreadsheet bietet dir eine 
                strukturierte Übersicht über alle relevanten Kategorien und Verkäufer.
              </p>
              <p>
                Das <strong>OOPBuy Spreadsheet</strong> wurde von erfahrenen Online-Shoppern entwickelt, 
                die die Herausforderungen des internationalen Einkaufs aus erster Hand kennen. 
                Besonders für den deutschen Markt haben wir das Spreadsheet mit spezifischen Informationen 
                zu Versandkosten, Zollbestimmungen und lokalen Zahlungsmethoden angereichert. 
                In diesem umfassenden Guide erfährst du Schritt für Schritt, wie du das volle Potenzial 
                des Spreadsheets ausschöpfst.
              </p>
            </section>

            {/* Section 2 */}
            <section id="was-ist-oopbuy">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Was Ist Das OOPBuy Spreadsheet Genau?
              </h2>
              <p>
                Das <strong>OOPBuy Spreadsheet</strong> ist mehr als nur eine einfache Tabelle - es ist 
                ein umfassendes Organisationssystem für Mode-Einkäufe aus internationalen Quellen. 
                Im Kern handelt es sich um eine strukturierte Datenbank, die Produkte aus 14 verschiedenen 
                Kategorien übersichtlich darstellt. Jede Kategorie enthält detaillierte Informationen zu 
                Preisen, Verkäuferbewertungen, Versandoptionen und Qualitätsmerkmalen.
              </p>
              <p>
                Anders als herkömmliche <strong>Spreadsheet</strong> Lösungen, die oft generisch und 
                unübersichtlich sind, wurde unser Tool speziell für den deutschsprachigen Raum konzipiert. 
                Das bedeutet: Alle Angaben sind in deutscher Sprache, Währungen werden automatisch 
                umgerechnet und Versandinformationen sind auf Deutschland zugeschnitten. Zudem werden 
                regelmäßig neue Produkte hinzugefügt und veraltete Einträge entfernt, sodass du stets 
                Zugriff auf aktuelle Informationen hast.
              </p>
            </section>

            {/* Section 3 */}
            <section id="kategorien">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Alle 14 Produktkategorien Im Überblick
              </h2>
              <p>
                Das Herzstück des <strong>OOPBuy Spreadsheet</strong> sind die 14 sorgfältig kuratierten 
                Produktkategorien. Jede Kategorie wurde basierend auf der Nachfrage deutscher Käufer 
                ausgewählt und wird kontinuierlich erweitert:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                {categoryDetails.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className={`${cat.icon} text-lg text-accent-600`}></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors font-[family-name:var(--font-label)]">
                        {cat.name}
                      </span>
                      <p className="text-xs text-foreground-500 font-[family-name:var(--font-body)]">
                        {cat.desc}
                      </p>
                    </div>
                  </a>
                ))}
              </div>

              <p>
                Jede dieser Kategorien im <strong>OOPBuy Spreadsheet Deutschland</strong> wird von 
                unserem Team regelmäßig gepflegt. Neue Produkte werden wöchentlich hinzugefügt, 
                Preise aktualisiert und nicht mehr verfügbare Artikel entfernt. So kannst du sicher 
                sein, immer die aktuellsten Informationen zu erhalten.
              </p>
            </section>

            {/* Section 4 */}
            <section id="sicherheit">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Sicherheit Und Vertrauen Beim OOPBuy Spreadsheet
              </h2>
              <p>
                Ein zentrales Anliegen des <strong>OOPBuy Spreadsheet</strong> Teams ist die Sicherheit 
                unserer Nutzer. Wir verstehen, dass der Kauf bei internationalen Verkäufern Fragen 
                aufwerfen kann. Deshalb haben wir umfangreiche Sicherheitsmechanismen in unser 
                Spreadsheet integriert.
              </p>

              <h3 className="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">
                Verifizierte Verkäufer
              </h3>
              <p>
                Jeder Verkäufer in unserem <strong>Spreadsheet</strong> durchläuft einen strengen 
                Verifizierungsprozess. Wir prüfen Bewertungen, Transaktionshistorien und 
                Produktqualität, bevor ein Verkäufer in unsere Liste aufgenommen wird. 
                Verkäufer mit negativen Bewertungen oder verdächtigem Verhalten werden umgehend entfernt.
              </p>

              <h3 className="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">
                Qualitätskontrolle (QC)
              </h3>
              <p>
                Das <strong>OOPBuy Spreadsheet</strong> bietet detaillierte Informationen zum 
                QC-Prozess. Bevor deine Bestellung nach Deutschland versendet wird, kannst du 
                Qualitätskontrollbilder anfordern. Diese zeigen den tatsächlichen Zustand des 
                Produkts und geben dir die Möglichkeit, Mängel vor dem Versand zu identifizieren.
              </p>

              <h3 className="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">
                Sichere Zahlungsmethoden
              </h3>
              <p>
                Unser Spreadsheet listet ausschließlich Verkäufer, die sichere und für Deutschland 
                optimierte Zahlungsmethoden anbieten. Von PayPal über Kreditkarte bis hin zu 
                deutschen Banküberweisungen - du hast die Wahl zwischen verschiedenen bewährten Optionen.
              </p>
            </section>

            {/* Section 5 */}
            <section id="versand">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Versand Nach Deutschland: Alles Was Du Wissen Musst
              </h2>
              <p>
                Der Versand nach Deutschland ist einer der wichtigsten Aspekte des <strong>OOPBuy 
                Spreadsheet</strong>. Wir haben umfangreiche Informationen zu Versandoptionen, 
                Lieferzeiten und Zollbestimmungen zusammengetragen, damit du genau weißt, was 
                dich erwartet.
              </p>
              <p>
                Die meisten Bestellungen über unser <strong>Spreadsheet Deutschland</strong> Netzwerk 
                erreichen dich innerhalb von 10-20 Werktagen. Expressversand ist bei vielen 
                Verkäufern gegen Aufpreis möglich und verkürzt die Lieferzeit auf 5-8 Werktage. 
                Unser Spreadsheet zeigt dir für jedes Produkt die verfügbaren Versandoptionen und 
                die geschätzten Kosten an.
              </p>
              <p>
                Besonders wichtig für deutsche Käufer: Das <strong>OOPBuy Spreadsheet</strong> 
                enthält detaillierte Informationen zu Zollgebühren und Einfuhrumsatzsteuer. 
                Wir erklären dir, ab welchem Warenwert zusätzliche Kosten anfallen und wie du 
                diese korrekt kalkulierst. So vermeidest du böse Überraschungen bei der Lieferung.
              </p>
            </section>

            {/* Section 6 */}
            <section id="vergleich">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                OOPBuy Spreadsheet Im Vergleich Zu Anderen Lösungen
              </h2>
              <p>
                Es gibt verschiedene <strong>Spreadsheet</strong> Angebote auf dem Markt, doch 
                unser OOPBuy Spreadsheet hebt sich in mehreren wichtigen Punkten von der Konkurrenz ab:
              </p>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left p-3 font-semibold text-foreground-800 font-[family-name:var(--font-label)]">Feature</th>
                      <th className="text-left p-3 font-semibold text-primary-600 font-[family-name:var(--font-label)]">OOPBuy Spreadsheet</th>
                      <th className="text-left p-3 font-semibold text-foreground-500 font-[family-name:var(--font-label)]">Andere Anbieter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, i) => (
                      <tr key={i} className="border-b border-background-200">
                        <td className="p-3 text-foreground-700 font-[family-name:var(--font-body)]">{row.feature}</td>
                        <td className="p-3 text-primary-600 font-semibold font-[family-name:var(--font-label)]">{row.oopbuy}</td>
                        <td className="p-3 text-foreground-500 font-[family-name:var(--font-body)]">{row.others}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 7 */}
            <section id="faq">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Häufig Gestellte Fragen (FAQ)
              </h2>

              <div className="space-y-4">
                {faqData.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer font-[family-name:var(--font-label)]">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed font-[family-name:var(--font-body)]">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Section 8 */}
            <section id="fazit">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                Fazit: Lohnt Sich Das OOPBuy Spreadsheet?
              </h2>
              <p>
                Nach unserer ausführlichen Analyse können wir mit Überzeugung sagen: Das <strong>OOPBuy 
                Spreadsheet</strong> ist ein unverzichtbares Tool für alle modebewussten Käufer in 
                Deutschland. Die Kombination aus umfassenden Produktkategorien, strengen 
                Sicherheitsstandards und deutschlandspezifischen Versandinformationen macht es zur 
                ersten Wahl für internationales Mode-Shopping.
              </p>
              <p>
                Besonders hervorzuheben ist die kontinuierliche Pflege des Spreadsheets. Anders als 
                statische Listen wird unser <strong>OOPBuy Spreadsheet Deutschland</strong> Angebot 
                wöchentlich aktualisiert, sodass du stets Zugang zu den neuesten Produkten und 
                besten Preisen hast. Die Integration von QC-Bildern und verifizierten Verkäufern 
                gibt dir zusätzliche Sicherheit bei jedem Kauf.
              </p>
              <p>
                Egal ob du ein erfahrener Shopper oder ein Neueinsteiger bist - das OOPBuy Spreadsheet 
                bietet dir den perfekten Einstieg in die Welt des internationalen Mode-Einkaufs. 
                Probiere es noch heute aus und entdecke die Vorteile eines strukturierten, 
                sicheren und transparenten Shopping-Erlebnisses.
              </p>
            </section>

            {/* CTA */}
            <div className="mt-12 p-8 rounded-2xl bg-primary-500 text-center">
              <h2 className="text-xl font-heading font-bold text-background-50 mb-3">
                Bereit Für Dein Nächstes Shopping-Erlebnis?
              </h2>
              <p className="text-sm text-primary-100 mb-6 font-[family-name:var(--font-body)]">
                Entdecke jetzt alle Kategorien im OOPBuy Spreadsheet und finde die besten Angebote.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-8 py-3.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]"
                >
                  Buy It Now
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center px-6 py-3.5 rounded-full border border-primary-300 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors font-[family-name:var(--font-label)]"
                >
                  Zurück Zur Startseite
                </Link>
              </div>
            </div>

            {/* Related Links */}
            <div className="mt-12 p-6 rounded-xl bg-background-100">
              <p className="text-xs text-foreground-500 mb-3 font-[family-name:var(--font-label)]">
                Das könnte dich auch interessieren:
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]"
                >
                  ← OOPBuy Spreadsheet Startseite
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]"
                >
                  kaspreadsheet.com
                </a>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 rounded-lg border border-background-200">
              <p className="text-xs text-foreground-400 leading-relaxed font-[family-name:var(--font-body)]">
                <strong>Haftungsausschluss:</strong> Dieser Artikel dient ausschließlich 
                Informationszwecken. Das OOPBuy Spreadsheet ist ein unabhängiges Tool und steht 
                in keiner Verbindung zu den genannten Marken oder Plattformen. Alle Produktnamen, 
                Marken und eingetragenen Warenzeichen sind Eigentum ihrer jeweiligen Inhaber. 
                Preise und Verfügbarkeiten können sich ändern.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="w-full bg-primary-900">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer font-[family-name:var(--font-body)]">
              ← Zurück zur OOPBuy Spreadsheet Startseite
            </Link>
            <p className="text-xs text-foreground-400 font-[family-name:var(--font-body)]">
              &copy; {new Date().getFullYear()} OOPBuy Spreadsheet. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

const tocItems = [
  { id: 'einleitung', title: '1. Einleitung: Warum das OOPBuy Spreadsheet Dein Shopping-Erlebnis Revolutioniert' },
  { id: 'was-ist-oopbuy', title: '2. Was Ist Das OOPBuy Spreadsheet Genau?' },
  { id: 'kategorien', title: '3. Alle 14 Produktkategorien Im Überblick' },
  { id: 'sicherheit', title: '4. Sicherheit Und Vertrauen Beim OOPBuy Spreadsheet' },
  { id: 'versand', title: '5. Versand Nach Deutschland: Alles Was Du Wissen Musst' },
  { id: 'vergleich', title: '6. OOPBuy Spreadsheet Im Vergleich Zu Anderen Lösungen' },
  { id: 'faq', title: '7. Häufig Gestellte Fragen (FAQ)' },
  { id: 'fazit', title: '8. Fazit: Lohnt Sich Das OOPBuy Spreadsheet?' },
];

const categoryDetails = [
  { name: 'Shoes', icon: 'ri-footprint-line', desc: 'Sneaker & Sportschuhe', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies/Sweaters', icon: 'ri-shirt-line', desc: 'Pullover & Kapuzenpullis', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', icon: 'ri-shirt-line', desc: 'Bedruckte & Basics', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', icon: 'ri-contrast-drop-line', desc: 'Jacken & Mäntel', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants/Shorts', icon: 'ri-pantone-line', desc: 'Hosen & Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', icon: 'ri-base-station-line', desc: 'Caps & Mützen', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Sets', icon: 'ri-stack-line', desc: 'Komplette Outfits', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'Underwear', icon: 'ri-checkbox-blank-line', desc: 'Unterwäsche & Basics', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'Jersey', icon: 'ri-shirt-line', desc: 'Sporttrikots', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'Accessories', icon: 'ri-vip-diamond-line', desc: 'Schmuck & Uhren', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', icon: 'ri-briefcase-line', desc: 'Taschen & Rucksäcke', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', icon: 'ri-link', desc: 'Gürtel & Accessoires', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'Perfumes', icon: 'ri-drop-line', desc: 'Parfums & Düfte', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'Other Stuff', icon: 'ri-more-line', desc: 'Sonstige Artikel', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

const comparisonData = [
  { feature: 'Deutsche Sprache', oopbuy: '✅ Vollständig', others: '❌ Meist Englisch' },
  { feature: '14 Kategorien', oopbuy: '✅ Kuratiert', others: '⚠️ Oft unstrukturiert' },
  { feature: 'Verifizierte Verkäufer', oopbuy: '✅ Geprüft', others: '❌ Keine Prüfung' },
  { feature: 'Deutschland Versand', oopbuy: '✅ Optimiert', others: '⚠️ Generisch' },
  { feature: 'QC Bilder Integration', oopbuy: '✅ Verfügbar', others: '❌ Nicht vorhanden' },
  { feature: 'Wöchentliche Updates', oopbuy: '✅ Regelmäßig', others: '❌ Selten bis nie' },
  { feature: 'Zollinformationen', oopbuy: '✅ Detailliert', others: '❌ Nicht enthalten' },
  { feature: 'Buyer Protection', oopbuy: '✅ Integriert', others: '⚠️ Eingeschränkt' },
];

const faqData = [
  {
    question: 'Was ist das OOPBuy Spreadsheet?',
    answer: 'Das OOPBuy Spreadsheet ist ein umfassendes Tool für internationale Mode-Einkäufe, das speziell für den deutschen Markt optimiert wurde. Es bietet 14 Produktkategorien mit detaillierten Informationen zu Preisen, Verkäufern und Versandoptionen.',
  },
  {
    question: 'Wie oft wird das Spreadsheet aktualisiert?',
    answer: 'Unser OOPBuy Spreadsheet wird wöchentlich aktualisiert. Neue Produkte werden hinzugefügt, Preise angepasst und nicht mehr verfügbare Artikel entfernt. So hast du stets Zugriff auf aktuelle Informationen.',
  },
  {
    question: 'Ist das OOPBuy Spreadsheet kostenlos?',
    answer: 'Ja, der Zugang zu unserem Spreadsheet und allen Kategorien ist vollständig kostenlos. Du kannst alle 14 Kategorien durchstöbern und direkt zu den besten Angeboten navigieren.',
  },
  {
    question: 'Wie funktioniert der Versand nach Deutschland?',
    answer: 'Das Spreadsheet enthält detaillierte Versandinformationen für Deutschland. Die meisten Bestellungen erreichen dich innerhalb von 10-20 Werktagen. Expressversand verkürzt die Lieferzeit auf 5-8 Werktage. Zollgebühren und Einfuhrumsatzsteuer werden transparent dargestellt.',
  },
  {
    question: 'Sind die Verkäufer im Spreadsheet vertrauenswürdig?',
    answer: 'Absolut. Alle Verkäufer durchlaufen einen strengen Verifizierungsprozess. Wir prüfen Bewertungen, Transaktionshistorien und Produktqualität. Verkäufer mit negativem Feedback werden umgehend entfernt.',
  },
  {
    question: 'Kann ich QC-Bilder vor dem Versand anfordern?',
    answer: 'Ja, das OOPBuy Spreadsheet unterstützt die Integration von QC-Bildern. Du kannst vor dem Versand Qualitätskontrollbilder anfordern, um den tatsächlichen Zustand deines Produkts zu überprüfen.',
  },
  {
    question: 'Welche Zahlungsmethoden werden unterstützt?',
    answer: 'Unser Spreadsheet listet Verkäufer mit verschiedenen sicheren Zahlungsmethoden: PayPal, Kreditkarte und deutsche Banküberweisungen. Alle Methoden sind für den deutschen Markt optimiert.',
  },
];